'use client';

import { usePathname } from 'next/navigation';
import { useState, type FC, type PropsWithChildren } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Box, Flex, type FlexProps, Text } from '@eqi-investimentos/react';
import { PATHS } from '@/config/paths';
import { floatingInvestScreensRoutes, routesSHowDisclaimerEqiResearchHubs } from '@/config/routesConfig';
import { ProfileDrawer } from '@/modules/Customer/ui/ProfileDrawer';
import { Card } from '@/modules/FinancialPlanning/ui/Widgets/Home';
import { DisclaimerResearchContent } from '@/modules/Research/ui/DisclaimerResearchContent/DisclaimerResearchContent';
import { FloatingButton } from '@/components/atoms/FloatingButton';
import { InvestFloatingButton } from '@/components/atoms/InvestFloatingButton';
import type { HeaderProps } from '@/components/molecules/Header';
import { Header } from '@/components/molecules/Header';
import { SideMenu } from '@/components/organisms/SideMenu';
import { IfRender } from '@/utils/jsx';
export const AuthTemplate: FC<AuthTemplateProps> = ({
  children,
  header,
  showDisclaimerContentResearch = false,
  containerProps,
  showFPFloatingButton = false
}) => {
  const pathname = usePathname();
  const {
    t
  } = useTranslation('common');
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const showHeader = typeof header?.show === 'undefined' ? true : header?.show;
  const cards = t<Card[]>('screens.financialPlanning.home.cards', null, {
    returnObjects: true
  });
  return <Box h="100vh" w="100vw" bgColor="background.primary" overflow="hidden" data-sentry-element="Box" data-sentry-component="AuthTemplate" data-sentry-source-file="index.tsx">
      <IfRender condition={showFPFloatingButton} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
        <FloatingButton options={cards} data-sentry-element="FloatingButton" data-sentry-source-file="index.tsx" />
      </IfRender>
      <Flex pos="relative" flexDir={{
      base: 'column',
      md: 'row'
    }} w="full" h="full" overflow="hidden" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
        <SideMenu isOpen={isSideMenuOpen} setIsMenuOpen={setIsSideMenuOpen} data-sentry-element="SideMenu" data-sentry-source-file="index.tsx" />
        <Flex flexDir="column" overflowY="scroll" overflowX="hidden" w={{
        base: 'full',
        md: isSideMenuOpen ? 'calc(100% - 19.625rem)' : 'calc(100% - 6.875rem)'
      }} h="full" transition="all 500ms ease-in-out" data-sentry-element="Flex" data-sentry-source-file="index.tsx">
          <IfRender condition={showHeader} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
            <Header {...header} data-sentry-element="Header" data-sentry-source-file="index.tsx" />
          </IfRender>
          <Flex direction="column" gap="6xl" p="2xl" w="full" maxW={{
          base: 'full',
          '2xl': '96rem'
        }} mx="auto" {...containerProps} data-sentry-element="Flex" data-sentry-source-file="index.tsx">
            {children}
          </Flex>
          <IfRender condition={checkCanShowInvestFloatingButton(pathname)} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
            <InvestFloatingButton data-sentry-element="InvestFloatingButton" data-sentry-source-file="index.tsx" />
          </IfRender>
          <IfRender condition={routesSHowDisclaimerEqiResearchHubs.includes(pathname)} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
            <Text fontSize="m" color="content.secondary" p="2xl" pt="0" sx={{
            '& a': {
              color: 'content.secondary',
              fontWeight: 'semibold',
              textDecoration: 'none'
            }
          }} dangerouslySetInnerHTML={{
            __html: t('app.disclaimerEqiResearchHub')
          }} data-sentry-element="Text" data-sentry-source-file="index.tsx" />
          </IfRender>
          <IfRender condition={showDisclaimerContentResearch} data-sentry-element="IfRender" data-sentry-source-file="index.tsx">
            <DisclaimerResearchContent data-sentry-element="DisclaimerResearchContent" data-sentry-source-file="index.tsx" />
          </IfRender>
        </Flex>
      </Flex>
      <ProfileDrawer data-sentry-element="ProfileDrawer" data-sentry-source-file="index.tsx" />
    </Box>;
};
const checkCanShowInvestFloatingButton = (pathname: string) => {
  if (pathname === PATHS.home) return false;
  const pathnameMatched = floatingInvestScreensRoutes.includes(pathname);
  if (pathnameMatched) return pathnameMatched;
  const [, basePath] = pathname.split('/');
  const hasMatchingPath = floatingInvestScreensRoutes.some(path => path.includes(basePath));
  return hasMatchingPath;
};
export type AuthTemplateProps = PropsWithChildren<{
  header?: AuthTemplateHeaderProps;
  showDisclaimerContentResearch?: boolean;
  containerProps?: FlexProps;
  showFPFloatingButton?: boolean;
  hasNoPrevRoute?: boolean;
}>;
type AuthTemplateHeaderProps = HeaderProps & {
  show?: boolean;
};